import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Container,
  TextField,
  Button,
  Box,
  MenuItem,
  styled,
  IconButton,
  InputAdornment
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import PhotoBox from '../layout/PhotoBox';
import { updateDoc, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../service/AuthContext';
import { storage, db, functions } from '../service/Firebase-config';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { updateProfile, getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const grades = [
  "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12", "University"
];

export default function DialogProfile({ open, handleClose, selectedUser, setSelectedUser }) {
  const { userInfo, setUserInfo } = useAuth();
  const auth = getAuth();
  const user = auth.currentUser;
  const isStudent = selectedUser?.role === "student";
  const isUser = userInfo?.role !== "admin";
  const dbCollection = isStudent ? 'students' : 'teachers';

  const [isLoading, setIsLoading] = useState(false);
  const [isPhotoLoading, setIsPhotoLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const formattedFullName = (originName, fullName) => {
    const name = originName;
    const lastDot = name.lastIndexOf('.');
    const ext = name.substring(lastDot + 1);
    return (fullName.replace(' ', "_") + "." + ext).toLowerCase();
  };

  const refetchUserInfo = async (updatedData) => {
    const selectedUserRef = doc(db, dbCollection, selectedUser.uid);
    await updateDoc(selectedUserRef, updatedData);
    const updatedInfo = await getDoc(selectedUserRef);
    setSelectedUser(updatedInfo.data());
    if (!isUser && userInfo.uid === selectedUser.uid) {
      setUserInfo(updatedInfo.data());
    }
  };

  const handleFileUpload = async (e) => {
    setIsPhotoLoading(true);
    const userPhotoRef = ref(storage, "photo/" + selectedUser.uid + "/" + formattedFullName(e.target.files[0]?.name, selectedUser.displayName));
    try {
      await uploadBytes(userPhotoRef, e.target.files[0]);
      const url = await getDownloadURL(userPhotoRef);
      if (isUser) {
        await updateProfile(user, { photoURL: url });
      } else {
        const updateUser = httpsCallable(functions, 'handleUpdateUser');
        await updateUser({ uid: selectedUser.uid, photoURL: url });
      }
      await refetchUserInfo({ photoUrl: url });
    } catch (error) {
      console.error(error);
    } finally {
      setIsPhotoLoading(false);
    }
  };

  const handleUpdate = async (updatedData) => {
    setIsLoading(true);
    if (!isUser) {
      const updateUser = httpsCallable(functions, 'handleUpdateUser');
      const payload = { uid: selectedUser.uid, displayName: updatedData.displayName };
      if (updatedData.password) {
        payload.password = updatedData.password;
      }
      await updateUser(payload);
    }
    await refetchUserInfo(updatedData);
    setIsLoading(false);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: 'form',
        onSubmit: (event) => {
          event.preventDefault();
          const formData = new FormData(event.currentTarget);
          const formJson = Object.fromEntries(formData.entries());
          handleUpdate(formJson);
        }
      }}
    >
      <DialogTitle>Edit {selectedUser?.displayName}'s Profile</DialogTitle>
      <DialogContent>
        <Container
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: "center",
            alignItems: "center",
            height: 250,
          }}
        >
          <Box sx={{ height: 200, width: 200 }}>
            <PhotoBox selectedUser={selectedUser} />
          </Box>
          {!isUser && (
            <LoadingButton
              loading={isPhotoLoading}
              component="label"
              tabIndex={-1}
            >
              Change Photo
              <VisuallyHiddenInput type="file" onChange={handleFileUpload} />
            </LoadingButton>
          )}
        </Container>
        <TextField
          required
          margin="dense"
          id="displayName"
          name="displayName"
          label="Full Name"
          fullWidth
          variant="outlined"
          defaultValue={selectedUser?.displayName}
          disabled={isUser}
        />
        <TextField
          required
          margin="dense"
          id="email"
          name="email"
          label="Email"
          fullWidth
          variant="outlined"
          value={selectedUser?.email}
          disabled
        />
        {userInfo?.role === "admin" && (
          <TextField
            margin="dense"
            id="password"
            name="password"
            label="New Password"
            fullWidth
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
        <TextField
          margin="dense"
          id="phone"
          name="phone"
          label="Phone Number"
          fullWidth
          variant="outlined"
          defaultValue={selectedUser?.phone}
          disabled={isUser}
        />
        {isStudent && (
          <>
            <TextField
              required
              margin="dense"
              id="parentPhone"
              name="parentPhone"
              label="Parent Phone Number"
              fullWidth
              variant="outlined"
              defaultValue={selectedUser?.parentPhone}
              disabled={isUser}
            />
            <TextField
              required
              margin="dense"
              id="school"
              name="school"
              label="School"
              fullWidth
              variant="outlined"
              defaultValue={selectedUser?.school}
              disabled={isUser}
            />
            <TextField
              required
              select
              fullWidth
              margin="dense"
              id="grade"
              name="grade"
              label="Grade"
              defaultValue={selectedUser?.grade}
              disabled={isUser}
            >
              {grades && grades.map((gradeItem) => (
                <MenuItem key={gradeItem} value={gradeItem}>
                  {gradeItem}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
        <TextField
          required
          margin="dense"
          id="branch"
          name="branch"
          label="Branch"
          fullWidth
          variant="outlined"
          defaultValue={selectedUser?.branch}
          disabled={isUser}
        />
        {userInfo?.role === "admin" && (
          <TextField
            margin="dense"
            id="info"
            name="info"
            label="Info"
            fullWidth
            variant="outlined"
            rows={3}
            multiline
            defaultValue={selectedUser?.info}
          />
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={isLoading} type="submit" disabled={isUser}>Save</LoadingButton>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
